/** Yahoo 코인 심볼(-USD) → 바이낸스 USDT 페어 (스트림용 소문자). 코인 아니면 null */
export function binanceSymbol(symbol: string): string | null {
  const m = /^([A-Z0-9]+)-USD$/.exec(symbol || '');
  return m ? `${m[1].toLowerCase()}usdt` : null;
}

/** Yahoo interval → 바이낸스 kline interval (미지원 null) */
export function binanceInterval(yahooInterval: string): string | null {
  const map: Record<string, string> = {
    '1m': '1m', '5m': '5m', '15m': '15m',
    '1d': '1d', '1wk': '1w', '1mo': '1M',
  };
  return map[yahooInterval] || null;
}

export type LiveCandle = { t: number; o: number; h: number; l: number; c: number; v: number };

/** kline 이벤트 → ohlcv route와 동일 형태 {t(초),o,h,l,c,v}. 형식 안 맞으면 null */
export function parseKline(input: unknown): LiveCandle | null {
  // WS 원본 메시지 — 읽는 필드만 느슨하게 본다(검증은 아래 t·isFinite 가드)
  const msg = input as { e?: unknown; k?: Record<string, unknown> | null } | null | undefined;
  const k = msg?.e === 'kline' ? msg.k : null;
  if (!k || k.t == null) return null;
  const c = {
    t: Math.floor(Number(k.t) / 1000),
    o: Number(k.o), h: Number(k.h), l: Number(k.l), c: Number(k.c), v: Number(k.v),
  };
  if (![c.o, c.h, c.l, c.c, c.v].every(Number.isFinite)) return null;
  return c;
}

/** Yahoo interval → 초 (월봉은 31일 상한 — bucket 비교용이라 과대값 무해) */
export function intervalSeconds(yahooInterval: string): number {
  const map: Record<string, number> = {
    '1m': 60, '5m': 300, '15m': 900,
    '1d': 86400, '1wk': 604800, '1mo': 2678400,
  };
  return map[yahooInterval] || 60;
}

/**
 * 바이낸스 틱을 차트 캔들에 병합 — ⚠ 단순 t 비교 금지 (2026-06-13 분봉 무시 버그):
 * Yahoo 진행봉 t는 "현재 시각"(예 20:54:13), 바이낸스 t는 봉 경계(20:54:00)라
 * t 동일성 비교 시 바이낸스 틱이 전부 "과거"로 분류돼 버려진다. interval bucket으로 비교.
 * - 같은 bucket → 마지막 봉 통째 교체(거래량 포함), t는 기존 Yahoo t 유지(시간축 안정)
 * - 미래 bucket → 새 봉 append (단 주·월봉은 epoch bucket 경계가 거래소 정렬과 달라 append 금지 — c/h/l만 병합)
 * - 과거 bucket → 무시 (재연결 직후 잔여 틱)
 */
export function mergeLiveCandle(cs: LiveCandle[], k: LiveCandle, intervalSec: number): LiveCandle[] {
  if (!cs.length) return cs;
  const last = cs[cs.length - 1];
  const kB = Math.floor(k.t / intervalSec);
  const lastB = Math.floor(last.t / intervalSec);
  if (kB === lastB) return [...cs.slice(0, -1), { ...k, t: last.t }];
  if (kB > lastB) {
    if (intervalSec > 86400) {
      // 주·월봉: bucket 어긋남 = 정렬 차이일 뿐 같은 봉 — 고저 확장·종가만 갱신 (v는 누적 기준 달라 유지)
      return [...cs.slice(0, -1), { ...last, c: k.c, h: Math.max(last.h, k.h), l: Math.min(last.l, k.l) }];
    }
    return [...cs, k];
  }
  return cs;
}

/**
 * 포트폴리오에 실시간 가격 반영 — 델타 방식 (2026-06-13 총자산 점프 버그 fix):
 * ⚠ 서버 total_value_bull = 현금 + 보유평가. 총계를 "포지션 합"으로 재계산하면 현금이 빠져
 *   1초 틱마다 작은 값 ↔ 30s 폴링마다 서버값으로 "엄청 큰 숫자" 점프가 발생했다 (효신님 제보).
 * 해결: 포지션 변화분 d만 총계 필드들에 가산 — 현금·매수원금은 자동 불변.
 *  - value_new = value × (price/base) → 환율 fx 보존 (연쇄 호출도 비율 약분으로 누적 오차 0)
 *  - holdings/total/pnl/unrealized 전부 += d (cost = holdings−unrealized 불변 증명: (h+d)−(u+d)=h−u)
 *  - pnl_pct: casual = unrealized/매수원금 · contest = pnl/시드 (서버 정의와 동일 분모)
 */
// 서버 포트폴리오 응답 중 이 함수가 읽는 필드 — 숫자는 문자열로 올 수 있어 Number()로 정규화한다
type LivePosition = { symbol?: string; cur_price?: unknown; value_bull?: unknown; pnl_bull?: unknown; pnl_pct?: unknown };
type LivePortfolio = {
  positions?: LivePosition[] | null;
  holdings_value_bull?: unknown; total_value_bull?: unknown; pnl_bull?: unknown;
  unrealized_pnl_bull?: unknown; pnl_pct?: unknown;
  account?: { wallet_mode?: unknown; starting_bull?: unknown } | null;
};
export function applyLivePrice<T extends LivePortfolio | null | undefined>(pf: T, symbol: string, price: number): T & LivePortfolio {
  // 반환은 입력과 같은 모양 + 총계 필드 갱신(pnl_pct 등이 원래 없던 입력이어도 붙는다)
  const idx = (pf?.positions || []).findIndex((p) => p.symbol === symbol);
  if (idx < 0) return pf as T & LivePortfolio;
  const src = pf as LivePortfolio & { positions: LivePosition[] }; // idx ≥ 0 이면 pf·positions 존재
  const pos = src.positions[idx];
  const base = Number(pos.cur_price);
  if (!Number.isFinite(base) || base <= 0 || !Number.isFinite(price) || price <= 0) return pf as T & LivePortfolio;
  const value = Number(pos.value_bull) * (price / base);
  const d = value - Number(pos.value_bull);
  const posCost = Number(pos.value_bull) - Number(pos.pnl_bull || 0);
  const posPnl = value - posCost;
  const next = {
    ...pos, cur_price: price, value_bull: value, pnl_bull: posPnl,
    pnl_pct: posCost > 0 ? (posPnl / posCost) * 100 : pos.pnl_pct,
  };
  const positions = [...src.positions.slice(0, idx), next, ...src.positions.slice(idx + 1)];
  const holdings = Number(src.holdings_value_bull || 0) + d;
  const total = Number(src.total_value_bull || 0) + d;       // 현금 보존
  const pnl = Number(src.pnl_bull || 0) + d;
  const unrealized = Number(src.unrealized_pnl_bull ?? src.pnl_bull ?? 0) + d;
  const mode = src.account?.wallet_mode;
  const starting = Number(src.account?.starting_bull || 0);
  const costSum = holdings - unrealized;                    // 보유 매수원금 (불변)
  const pnlPct = mode === 'contest' && starting > 0
    ? (pnl / starting) * 100
    : costSum > 0 ? (unrealized / costSum) * 100 : src.pnl_pct;
  return {
    ...src, positions,
    holdings_value_bull: holdings, total_value_bull: total,
    pnl_bull: pnl, unrealized_pnl_bull: unrealized, pnl_pct: pnlPct,
  } as T & LivePortfolio;
}
